import map from 'lodash/map'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

import { i18nDateToString } from 'modules/core/lib/dates'

import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type CommonSelectorsForm from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import { Shift } from 'modules/shift_management/lib/types'

import {
  useDefaultAttributesFromRows,
  useDefaultAttributesFromRowsGraphQL,
} from './useDefaultAttributesFromRows'
import useRowAndEmployeeSelection from './useRowAndEmployeeSelection'
import { useSelectionState } from './useSelection'

const useEditEmployees = (
  shifts: Shift[],
  mixedValues: boolean,
  shiftsEmployeesIds: string[],
  form: CommonSelectorsForm
) => {
  const [{ view }] = useSchedule()
  const { daysByRow: selectedDaysByRow } = useSelectionState()
  const daysByRow =
    Object.keys(selectedDaysByRow).length > 0
      ? selectedDaysByRow
      : shifts.reduce<Record<string, string[]>>((acc, shift) => {
          const rowKey = shift.rowKeyReference(view)
          const days = acc[rowKey] || []

          acc[rowKey] = uniq([...days, i18nDateToString(shift.date, 'backend')])
          return acc
        }, {})

  // We show the employee selector only if editing shifts from the same employee
  // and we need to include the original employee in case the user wants to
  // edit original and create new ones for other employees at the same time

  const originalId = shiftsEmployeesIds.length === 1 ? shiftsEmployeesIds[0] : undefined
  const selectedEmployeeIds = form.employeeSelection?.ids ?? []

  const editingOneEmployee = shiftsEmployeesIds.length === 1
  const selectingOneEmployee = selectedEmployeeIds.length === 1
  const editingOriginal =
    editingOneEmployee && selectingOneEmployee && originalId === selectedEmployeeIds[0]

  const allEmployees = form.employeeSelection?.type === 'all'

  // This is needed to separate the actions between edit + create OR delete + create
  const isOriginalIncluded =
    (originalId !== undefined && selectedEmployeeIds.includes(originalId)) ||
    form.employeeSelection?.type === 'all'

  // As daysByRow depend on the selection reducer and only the original employee
  // is included, we need to use the method below with the new ids from the
  // employee selector input to create the days for those employees
  const reassignEmployeesIds = selectedEmployeeIds.filter((id: string) => id !== originalId)

  // For the custom shifts, we do not want to include the original employee
  // as we edit the original shift in a separate action from creating new shifts
  const daysByEmployeeCustom = useRowAndEmployeeSelection(
    allEmployees,
    daysByRow,
    reassignEmployeesIds
  )

  // For the saved shifts, we delete all original shifts and re-create them
  // from scratch, so the final id list will depend on the user selection
  // if we are editing shifts from only 1 employee
  const finalEmployeesIds = isOriginalIncluded
    ? uniq([...shiftsEmployeesIds, ...selectedEmployeeIds])
    : reassignEmployeesIds

  // To know if we are editing multiple employees, we check if
  // originalId exists. If it does, we need the finalEmployees logic above.
  // Otherwise, all we need is the daysByRow coming from the selection reducer
  const withOriginalEmployee = useRowAndEmployeeSelection(
    allEmployees,
    daysByRow,
    finalEmployeesIds
  )
  const daysByEmployeeSaved = originalId ? withOriginalEmployee : daysByRow

  const preselectedSavedShifts = map(uniqBy(shifts, 'defaultShiftId'), 'defaultShiftId')
  const mixedDefaultShifts = preselectedSavedShifts.length > 1
  const mixedTemplateShifts = map(uniqBy(shifts, 'templateWeekId'), 'templateWeekId').length > 1

  const canReassignShifts =
    editingOneEmployee && !mixedValues && !mixedDefaultShifts && !mixedTemplateShifts

  const showReassignMessage = editingOneEmployee && !editingOriginal

  const defaultAttributes = useDefaultAttributesFromRows()
  const defaultAttributesGraphQL = useDefaultAttributesFromRowsGraphQL()

  const hasMultipleEmployees = shiftsEmployeesIds.length > 1
  const selectedAreas = Object.values(defaultAttributes).flat()
  const hasMultipleAreas =
    uniqBy(selectedAreas, (x) => [x.location_id, x.work_area_id].join('-')).length > 1

  const canReplaceShifts = view.isByEmployees || (!hasMultipleEmployees && !hasMultipleAreas)

  return {
    editingOneEmployee,
    editingOriginal,
    isOriginalIncluded,
    daysByEmployeeCustom,
    daysByEmployeeSaved,
    canReassignShifts,
    canReplaceShifts,
    showReassignMessage,
    preselectedSavedShifts,
    /**
     * @deprecated Use defaultAttributesGraphQL instead, which works using mutations.
     */
    defaultAttributes,
    defaultAttributesGraphQL,
  }
}

export default useEditEmployees
